import React, { useState, useEffect } from "react";
import { X, Gift } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useIsMobile } from "@/hooks/use-mobile";
import { ALL_LOCATIONS } from "@/lib/locations";
import { cn } from "@/lib/utils";

import { useLocation as useRouterLocation } from "react-router-dom";

export const MobileDiscountPopup = () => {
  const [isOpen, setIsOpen] = useState(false);
  const isMobile = useIsMobile();
  const routerLocation = useRouterLocation();
  const path = routerLocation.pathname;

  let resolvedLocation = { name: "Sacramento", regionSlug: "sacramento-area" };
  const pathParts = path.split('/').filter(Boolean);
  const lastPart = pathParts[pathParts.length - 1];

  if (path.includes('/los-angeles') || path.includes('/la/')) resolvedLocation = { name: "Los Angeles", regionSlug: "los-angeles" };
  else if (path.includes('/san-francisco')) resolvedLocation = { name: "San Francisco", regionSlug: "san-francisco" };
  else if (path.includes('/san-diego')) resolvedLocation = { name: "San Diego", regionSlug: "san-diego" };
  else if (path.includes('/orange-county') || path.includes('/irvine') || path.includes('/anaheim') || path.includes('/huntington-beach') || path.includes('/santa-ana')) resolvedLocation = { name: "Orange County", regionSlug: "orange-county" };
  else if (path.includes('/santa-clara') || path.includes('/san-jose') || path.includes('/silicon-valley')) resolvedLocation = { name: "Santa Clara", regionSlug: "santa-clara" };
  else if (path.includes('/alameda') || path.includes('/oakland') || path.includes('/berkeley') || path.includes('/fremont') || path.includes('/hayward') || path.includes('/pleasanton') || path.includes('/dublin') || path.includes('/livermore') || path.includes('/tri-valley') || path.includes('/east-bay')) resolvedLocation = { name: "Alameda County", regionSlug: "alameda" };
  else if (path.includes('/contra-costa') || path.includes('/walnut-creek') || path.includes('/san-ramon')) resolvedLocation = { name: "Contra Costa", regionSlug: "contra-costa" };
  else if (path.includes('/marin')) resolvedLocation = { name: "Marin County", regionSlug: "marin" };
  else if (path.includes('/solano') || path.includes('/vallejo') || path.includes('/fairfield') || path.includes('/vacaville') || path.includes('/benicia')) resolvedLocation = { name: "Solano County", regionSlug: "solano" };

  const isEs = routerLocation.pathname.startsWith('/es');
  const isRu = routerLocation.pathname.startsWith('/ru');
  const isZh = routerLocation.pathname.startsWith('/zh');

  const textTitle = isZh ? "等等！领取 10% 折扣" : isRu ? "Подождите! Скидка 10%" : isEs ? "¡Espera! Obtén 10% de descuento" : "Wait! Grab 10% Off";
  const textDesc = isZh ? `在 ${resolvedLocation.name} 获得首次专业清洁 10% 折扣。结账时使用优惠码：` : isRu ? `Получите скидку 10% на первую профессиональную уборку в ${resolvedLocation.name}. Используйте код при оплате:` : isEs ? `Obtén 10% de descuento en tu primera limpieza profesional en ${resolvedLocation.name}. Usa el código al pagar:` : `Get 10% off your first professional clean in ${resolvedLocation.name}. Use code at checkout:`;
  const textClaim = isZh ? "领取我的折扣" : isRu ? "Получить мою скидку" : isEs ? "Reclamar Mi Descuento" : "Claim My Discount";
  const textNoThanks = isZh ? "不，谢谢，我愿意付全款" : isRu ? "Нет, спасибо, я заплачу полную стоимость" : isEs ? "No gracias, pagaré el precio completo" : "No thanks, I'll pay full price";
  const textDisclaimer = isZh ? `*仅对 ${resolvedLocation.name} 地区的新客户有效。` : isRu ? `*Действительно только для новых клиентов в районе ${resolvedLocation.name}.` : isEs ? `*Válido solo para clientes nuevos en el área de ${resolvedLocation.name}.` : `*Valid for new customers in the ${resolvedLocation.name} area only.`;

  useEffect(() => {
    if (!isMobile) return;

    const hasSeenPopup = localStorage.getItem("hasSeenMobileDiscount");
    if (!hasSeenPopup) {
      const timer = setTimeout(() => {
        setIsOpen(true);
      }, 3000); // Show after 3 seconds
      return () => clearTimeout(timer);
    }
  }, [isMobile]);

  const handleClose = () => {
    setIsOpen(false);
    localStorage.setItem("hasSeenMobileDiscount", "true");
  };

  const handleClaim = () => {
    document.dispatchEvent(new Event('open-booking'));
    handleClose();
  };

  if (!isOpen || !isMobile) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 animate-in fade-in duration-300">
      <div className="relative w-full max-w-sm bg-card rounded-2xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300 border border-border">
        {/* Close Button */}
        <button
          onClick={handleClose}
          aria-label="Close discount popup"
          className="absolute right-4 top-4 p-1 rounded-full bg-muted/50 text-muted-foreground hover:text-foreground transition-colors z-10"
        >
          <X className="h-5 w-5" aria-hidden="true" />
        </button>

        <div className="p-8 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 text-primary mb-6">
            <Gift className="h-8 w-8" />
          </div>

          <h2 className="text-2xl font-bold mb-2 tracking-tight">{textTitle}</h2>
          <p className="text-muted-foreground mb-6">
            {textDesc}
          </p>

          <div className="bg-muted rounded-lg p-4 mb-8 border-2 border-dashed border-primary/30">
            <span className="text-2xl font-mono font-bold text-primary tracking-widest uppercase">
              UPLEVEL10
            </span>
          </div>

          <div className="space-y-3">
            <Button
              onClick={handleClaim}
              className="w-full h-12 text-lg font-bold bg-primary text-primary-foreground hover:bg-primary-hover shadow-lg shadow-primary/20"
            >
              {textClaim}
            </Button>
            <button
              onClick={handleClose}
              className="text-sm text-muted-foreground hover:text-foreground underline underline-offset-4"
            >
              {textNoThanks}
            </button>
          </div>
        </div>

        <div className="bg-muted/50 p-4 text-center border-t border-border">
          <p className="text-xs text-muted-foreground">
            {textDisclaimer}
          </p>
        </div>
      </div>
    </div>
  );
};
